import { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Table, Button, Modal, Spinner, Badge, Form } from "react-bootstrap";
import visitasService from "../../../api/visitas/visitasService";
import prestadoresService from "../../../api/pessoas/prestadoresService";
import { FaPlus, FaEdit, FaEye, FaEraser, FaTrash, FaFileExcel } from "react-icons/fa";
import { getCurrentUser, formataDataHoraExibicao } from '../../../common/utils';
import VisitasEdit from "./VisitasEdit";
import Paginacao from '../../../common/pagination';
import { IMaskInput } from "react-imask";
import AsyncSelect from 'react-select/async';
import debounce from 'lodash.debounce';
import Swal from 'sweetalert2';

const Visitas = () => {
    const [visitas, setVisitas] = useState({});
    const [loadingData, setLoadingData] = useState(true);
    const [pageNumb, setPageNumb] = useState(1);
    
    // Filtros
    const getDefaultDates = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        const formatDisplayDate = (date) => {
            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
        };

        return {
            data_inicio: formatDisplayDate(firstDay),
            data_fim: formatDisplayDate(lastDay),
            medico_nome: ''
        };
    };

    const [filtros, setFiltros] = useState(getDefaultDates());

    // Controle do Modal de Edição
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Controle do Modal de Histórico
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedMedico, setSelectedMedico] = useState(null);
    
    const dados_usuario = getCurrentUser();
    const programa_id = dados_usuario['ambiente_programa_id'];

    const listarVisitas = useCallback(async (pagina = 1) => {
        setLoadingData(true);
        try {
            const toApiDate = (dateStr) => {
                if (!dateStr || dateStr.includes('_')) return '';
                const parts = dateStr.split('/');
                if (parts.length !== 3) return '';
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
            };

            const dataInic = toApiDate(filtros.data_inicio);
            const dataFim = toApiDate(filtros.data_fim);

            const params = {
                programa_id,
                page: pagina,
                group_by_medico: true
            };
            if (dataInic) params.data_inicio = dataInic;
            if (dataFim) params.data_fim = dataFim;
            if (filtros.medico_nome) params.medico_nome = filtros.medico_nome;
            
            const response = await visitasService.getAll(params);
            setVisitas(response.data);
            setPageNumb(pagina);
        } catch (error) {
            console.error("Erro lista visitas", error);
        } finally {
            setLoadingData(false);
        }
    }, [filtros, programa_id]);

    useEffect(() => {
        const handler = setTimeout(() => {
            listarVisitas(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [listarVisitas]);

    const handlePageChange = (num_page) => {
        listarVisitas(num_page);
    };

    const handleNew = () => {
        setEditingId(null);
        setShowModal(true);
    };

    const handleEdit = (id) => {
        setEditingId(id);
        setShowModal(true);
    };

    const handleViewHistory = async (medicoId, medicoNome) => {
        setSelectedMedico(medicoNome);
        setShowHistoryModal(true);
        setLoadingHistory(true);
        try {
            const response = await visitasService.getAll({ programa_id, cliente_id: medicoId });
            setHistoryData(response.data.results || []);
        } catch (error) {
            console.error("Erro ao carregar histórico", error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleDelete = async (registroId) => {
        Swal.fire({
            title: 'Excluir Visita',
            text: 'Tem certeza que deseja excluir esta visita? Esta ação não pode ser desfeita.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await visitasService.delete(registroId);
                    Swal.fire('Excluído!', 'A visita foi excluída com sucesso.', 'success');
                    listarVisitas(pageNumb);
                } catch (error) {
                    console.error("Erro ao excluir", error);
                    Swal.fire('Erro', 'Não foi possível excluir a visita.', 'error');
                }
            }
        });
    };

    const onSaveSuccess = () => {
        setShowModal(false);
        listarVisitas(pageNumb);
    };

    const handleExport = async () => {
        try {
            const toApiDate = (dateStr) => {
                if (!dateStr || dateStr.includes('_')) return '';
                const parts = dateStr.split('/');
                if (parts.length !== 3) return '';
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
            };

            const dataInic = toApiDate(filtros.data_inicio);
            const dataFim = toApiDate(filtros.data_fim);

            const params = {
                programa_id
            };
            if (dataInic) params.data_inicio = dataInic;
            if (dataFim) params.data_fim = dataFim;
            if (filtros.medico_nome) params.medico_nome = filtros.medico_nome;
            
            const response = await visitasService.exportCSV(params);
            
            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `relatorio_visitas_${dataInic || 'geral'}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Erro exportar visitas", error);
            Swal.fire('Erro', 'Não foi possível exportar o relatório.', 'error');
        }
    };

    return (
        <div className="container-fluid">
            <Row className="align-items-center mb-3 mt-3">
                <Col>
                    <h4 className="page-title mb-0">Relatório de Visitas</h4>
                </Col>
                <Col className="text-end">
                    <Button variant="secondary" onClick={handleNew}>
                        <FaPlus className="me-2"/> Nova Visita
                    </Button>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Card className="cardDestaque border-0 shadow-sm">
                        <Card.Body>
                            <h4 className="mb-3">Filtros</h4>
                            <Form onSubmit={(e) => { e.preventDefault(); listarVisitas(1); }}>
                                <Row className="align-items-end g-3">
                                    <Col sm={5} xs={12}>
                                        <Form.Group>
                                            <Form.Label>Médico</Form.Label>
                                            <AsyncSelect
                                                cacheOptions
                                                defaultOptions
                                                placeholder="Buscar médico..."
                                                isClearable
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        fontSize: '0.875rem'
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        padding: '0 8px'
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0px'
                                                    })
                                                }}
                                                loadOptions={debounce(async (inputValue, callback) => {
                                                    if (!inputValue) {
                                                        callback([]);
                                                        return;
                                                    }
                                                    try {
                                                        const response = await prestadoresService.getAll({
                                                            nome: inputValue,
                                                            programa_id,
                                                            categoria: 'medicos|multiprofissionais',
                                                            is_active: 1
                                                        });
                                                        const options = response.data.results.map(m => ({ value: m.nome, label: m.nome }));
                                                        callback(options);
                                                    } catch (e) {
                                                        callback([]);
                                                    }
                                                }, 500)}
                                                onChange={(opt) => setFiltros({ ...filtros, medico_nome: opt ? opt.value : '' })}
                                                value={filtros.medico_nome ? { value: filtros.medico_nome, label: filtros.medico_nome } : null}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col sm={3} xs={6}>
                                        <Form.Group>
                                            <Form.Label>De</Form.Label>
                                            <Form.Control 
                                                as={IMaskInput}
                                                mask="00/00/0000"
                                                placeholder="DD/MM/AAAA"
                                                value={filtros.data_inicio} 
                                                onAccept={(value) => setFiltros({...filtros, data_inicio: value})} 
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col sm={3} xs={6}>
                                        <Form.Group>
                                            <Form.Label>Até</Form.Label>
                                            <Form.Control 
                                                as={IMaskInput}
                                                mask="00/00/0000"
                                                placeholder="DD/MM/AAAA"
                                                value={filtros.data_fim} 
                                                onAccept={(value) => setFiltros({...filtros, data_fim: value})} 
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mt-3">
                                    <Col className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <Button 
                                                type="button"
                                                variant="light" 
                                                size="sm"
                                                onClick={() => setFiltros(getDefaultDates())}
                                                className="border"
                                            >
                                                <FaEraser /> Limpar Filtro
                                            </Button>
                                        </div>
                                        <Button 
                                            type="button"
                                            variant="success" 
                                            size="sm"
                                            onClick={handleExport}
                                        >
                                            <FaFileExcel /> Exportar Excel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="cardDestaque border-0 shadow-sm">
                <Card.Body>
                    {loadingData ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                    ) : (
                        <>
                            <Table responsive className="custom-table">
                                <thead>
                                    <tr>
                                        <th>Última Visita</th>
                                        <th>Médico</th>
                                        <th>Origem / Via</th>
                                        <th>Relevante?</th>
                                        <th>Patologia</th>
                                        <th className="text-end">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(!visitas.results || visitas.results.length === 0) && (
                                        <tr><td colSpan="6" className="text-center text-muted">Nenhuma visita registrada.</td></tr>
                                    )}
                                    {visitas.results && visitas.results.map((v) => (
                                        <tr key={v.id}>
                                            <td>{formataDataHoraExibicao(v.data)}</td>
                                            <td>
                                                <div className="fw-bold">{v.medico_nome}</div>
                                                <div className="small text-muted">
                                                    {v.medico_registro || v.medico_uf 
                                                        ? `${v.medico_registro || '-'} / ${v.medico_uf || '-'}` 
                                                        : ''
                                                    }
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg="secondary" className="me-1">{v.origem_nome}</Badge>
                                                <Badge bg="info">{v.via_nome}</Badge>
                                            </td>
                                            <td>
                                                {v.informacoes_relevantes 
                                                    ? <Badge bg="warning" text="dark">Sim</Badge> 
                                                    : <Badge bg="light" text="dark">Não</Badge>
                                                }
                                            </td>
                                            <td>{v.patologia_nome || '-'}</td>
                                            <td className="text-end">
                                                <Button variant="outline-dark" size="sm" className="me-1" title="Ver Histórico" onClick={() => handleViewHistory(v.medico, v.medico_nome)}>
                                                    <FaEye />
                                                </Button>
                                                <Button variant="outline-primary" size="sm" className="me-1" title="Editar" onClick={() => handleEdit(v.id)}>
                                                    <FaEdit />
                                                </Button>
                                                <Button variant="outline-danger" size="sm" title="Excluir" onClick={() => handleDelete(v.id)}>
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {visitas.results && visitas.results.length > 0 && (
                                <Paginacao 
                                    dados={visitas} 
                                    loading={loadingData} 
                                    page_numb={pageNumb} 
                                    _onClickPagina={handlePageChange} 
                                />
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal de Cadastro/Edição */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" backdrop="static">
                <Modal.Header closeButton><Modal.Title>{editingId ? 'Editar' : 'Registrar'} Visita</Modal.Title></Modal.Header>
                <Modal.Body>
                    <VisitasEdit 
                        id={editingId} 
                        programa_id={programa_id} 
                        onSuccess={onSaveSuccess} 
                        onCancel={() => setShowModal(false)}
                    />
                </Modal.Body>
            </Modal>

            {/* Modal de Histórico de Visitas */}
            <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Histórico de Visitas: {selectedMedico}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {loadingHistory ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                    ) : (
                        historyData.length === 0 ? (
                            <div className="text-center text-muted py-3">Nenhuma visita encontrada.</div>
                        ) : (
                            <div className="history-timeline">
                                {historyData.map((h) => (
                                    <Card key={h.id} className="mb-3 border-0 bg-light">
                                        <Card.Header className="bg-white border-bottom-0 d-flex justify-content-between align-items-center">
                                            <span className="fw-bold text-dark">{formataDataHoraExibicao(h.data)}</span>
                                            <div>
                                                <Badge bg="secondary" className="me-1">{h.origem_nome}</Badge>
                                                <Badge bg="info">{h.via_nome}</Badge>
                                            </div>
                                        </Card.Header>
                                        <Card.Body className="pt-0">
                                            <hr className="mt-0" />
                                            <Row className="mb-2">
                                                <Col md={12}>
                                                    <div className="small fw-bold text-muted mb-1">Checklist:</div>
                                                    <div className="d-flex align-items-center">
                                                <div className="d-flex flex-wrap gap-1 me-auto">
                                                    {h.informacoes_relevantes && <Badge bg="warning" text="dark" className="x-small">Info. Relevantes</Badge>}
                                                    {h.farmacovigilancia && <Badge bg="danger" className="x-small">Farmacovigilância</Badge>}
                                                    {h.paciente_suspeito && <Badge bg="outline-secondary" className="x-small text-muted">Pac. Suspeito</Badge>}
                                                    {h.paciente_diagnosticado && <Badge bg="outline-secondary" className="x-small text-muted">Pac. Diagnosticado</Badge>}
                                                    {h.paciente_em_crise && <Badge bg="outline-secondary" className="x-small text-muted">Pac. Crise</Badge>}
                                                    {h.usou_usando_medicacao && <Badge bg="outline-secondary" className="x-small text-muted">Usou/Usando Med.</Badge>}
                                                    {h.com_prescricao && <Badge bg="outline-secondary" className="x-small text-muted">Com Prescrição</Badge>}
                                                    {h.exames_solicitados_psd && <Badge bg="outline-secondary" className="x-small text-muted">Exames PSD</Badge>}
                                                </div>
                                                <Button 
                                                    variant="link" 
                                                    className="text-danger p-0 ms-2" 
                                                    title="Excluir Visita"
                                                    onClick={() => handleDelete(h.id)}
                                                >
                                                    <FaTrash size={14} />
                                                </Button>
                                            </div>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={12} className="mb-2">
                                                    <div className="small fw-bold text-muted">Descrição:</div>
                                                    <div className="bg-white p-2 rounded border small">{h.descricao}</div>
                                                </Col>
                                                {h.documentos_solicitados && (
                                                    <Col md={12} className="mb-2">
                                                        <div className="small fw-bold text-muted">Documentos:</div>
                                                        <div className="bg-white p-2 rounded border small">{h.documentos_solicitados}</div>
                                                    </Col>
                                                )}
                                                {h.observacoes && (
                                                    <Col md={12}>
                                                        <div className="small fw-bold text-muted">Observações:</div>
                                                        <div className="bg-white p-2 rounded border small">{h.observacoes}</div>
                                                    </Col>
                                                )}
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        )
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>Fechar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Visitas;
